/**
 * Product-authored additions for legacy three-panel blocks.
 *
 * The shared More Info pages use six substantial panels in two full-width rows.
 * These entries complete older three-item blocks without cloning or inventing a
 * generic fourth, fifth and sixth card. Every addition is tied to the product's
 * published capability, access, source or operating boundary.
 */

const card = (sub, title, text) => ({ sub, title, text });
const fact = (label, value) => ({ label, value });

const plannedFacts = (execution) => [
  fact('Capacity', 'Four published company tiers'),
  fact('Features', 'The same core feature set at every tier'),
  fact('Execution', execution),
];

export const PRODUCT_PANEL_ADDITIONS = {
  'backup-utility:about:facts': [
    fact('Projects', 'One-off or scheduled backup projects'),
    fact('Verification', 'Run history and verification reports'),
    fact('Restore', 'Restore to a destination you choose'),
  ],
  'pdf-toolkit:about:facts': [
    fact('Core jobs', 'Merge, split, compress, rotate and extract'),
    fact('History', 'Every operation is recorded'),
    fact('Boundary', 'No online converter is required'),
  ],
  'image-toolkit:about:facts': [
    fact('Input', 'One image or a whole folder'),
    fact('Control', 'Purpose, dimensions, crop, format and quality'),
    fact('Privacy', 'Metadata can be removed locally'),
  ],
  'usb-builder:about:facts': [
    fact('Integrity', 'SHA-256 verification and versioned manifests'),
    fact('Profiles', 'Reusable build profiles and incremental updates'),
    fact('Boundary', 'Destructive boot writing is not enabled'),
  ],
  'presentation-studio:about:facts': [
    fact('Input', 'Figures, rough points or an existing deck'),
    fact('Resilience', 'Local autosave and recovery'),
    fact('Privacy', 'Nothing is uploaded'),
  ],
  'image-composer:about:facts': [
    fact('Canvas', 'Custom size with several source images'),
    fact('Controls', 'Move, resize, rotate, layer, crop and finish'),
    fact('Handoff', 'Export, copy or continue in Presentation Studio'),
  ],
  'people-explorer:about:facts': [
    fact('Coverage', 'People, works, cast and crew'),
    fact('Evidence', 'Confidence and source conflicts remain visible'),
    fact('Export', 'Copy the current people or cast view to CSV'),
  ],

  'animal-explorer:what:cards': [
    card('Names', 'Synonyms resolve without hiding them', 'Common names, scientific names, synonyms and close spellings can reach the accepted record while the matched name remains visible.'),
    card('Comparison', 'Only compatible facts line up', 'Related animals are compared on shared fields, and missing or incompatible evidence stays explicit instead of becoming a false match.'),
    card('Media', 'Images retain their licence', 'Licensed images remain connected to provider and licence information so discovery does not erase where the media came from.'),
  ],
  'animal-explorer:about:facts': [
    fact('Search', 'Common names, scientific names and synonyms'),
    fact('Comparison', 'Related animals on compatible evidence'),
    fact('Favourites', 'Kept locally on your device'),
  ],
  'plex-discovery:about:facts': [
    fact('Library', 'Recommendations come from your own Plex data'),
    fact('Taste', 'Permanent taste and tonight\'s mood stay separate'),
    fact('Playback', 'Handed back to Plex'),
  ],
  'music-explorer:what:cards': [
    card('Chronology', 'Discographies stay in order', 'Albums, EPs, singles, releases and appearances remain connected to their dates and editions.'),
    card('Credits', 'Works and recordings stay distinct', 'Writer, performer and production relationships attach to the entity they actually describe.'),
    card('Discovery', 'Follow the connected people', 'Move from an artist to current and former members, collaborators, labels and related artists without losing the source trail.'),
  ],
  'music-explorer:about:facts': [
    fact('Boundary', 'Metadata only: no streaming or lyrics'),
    fact('Relationships', 'Members, credits, releases and labels'),
    fact('Favourites', 'Kept locally on your device'),
  ],
  'world-explorer:what:cards': [
    card('Hierarchy', 'Places stay inside their geography', 'Countries, regions, cities, landmarks and natural features retain the hierarchy that makes each result understandable.'),
    card('Measures', 'Population keeps its year', 'Population and comparable place facts retain their measurement date rather than presenting an undated number as current.'),
    card('Context', 'Maps and local time stay useful', 'Local maps and time-zone context support exploration without asking for or tracking the device location.'),
  ],
  'world-explorer:about:facts': [
    fact('Search', 'Countries, cities, regions and features'),
    fact('Time', 'Current local time computed on your device'),
    fact('Favourites', 'Kept locally on your device'),
  ],
  'screen-manager:what:cards': [
    card('Toolbars', 'Reserved space stays protected', 'Persistent toolbars occupy deliberate display space so other windows do not open underneath them.'),
    card('Applications', 'Windows remember their areas', 'Assigned applications return to the work area selected for them instead of drifting across the display.'),
    card('Profiles', 'Different layouts remain reusable', 'Save named profiles for different work and recover the intended layout when a monitor reconnects.'),
  ],
  'screen-manager:about:facts': [
    fact('Profiles', 'Reusable layouts for different work'),
    fact('Recovery', 'Reconnect a monitor without rebuilding the layout'),
    fact('Control', 'Draw areas visually, without coordinates'),
  ],
  'agent-control:what:cards': [
    card('Preflight', 'Check before an agent starts', 'Required files, scope and safeguards can be checked before work begins, so a task does not start from an unverified setup.'),
    card('Completion', 'Define what done must prove', 'Completion checks state the evidence an agent must produce before it reports that work is finished.'),
    card('Current task', 'Keep the active job visible', 'The current task remains explicit, helping rules and checks stay attached to the work actually being performed.'),
  ],
  'agent-control:about:facts': [
    fact('Scope', 'All projects, one project or the current task'),
    fact('Evidence', 'Ready only after PC and project checks'),
    fact('AI boundary', 'No AI model or API key inside Agent Control'),
  ],
  'email-client:what:cards': [
    card('Duplicates', 'Import does not multiply old mail', 'Duplicate protection keeps repeated PST imports from creating another copy of the same message.'),
    card('Everyday mail', 'Send, reply and forward directly', 'Attachments, drafts and the everyday message actions stay available without adding a crowded interface.'),
    card('Devices', 'Windows and Android share the simple model', 'Mail remains straightforward across desktop, phone and tablet, while PST import stays a Windows operation.'),
  ],
  'email-client:about:facts': [
    fact('Import', 'One-time Outlook PST import on Windows'),
    fact('Rules', 'Run on new mail or existing mail'),
    fact('Everyday work', 'Send, reply, forward, attach and draft'),
  ],

  'book-explorer:what:cards': [
    card('Identity', 'Work and edition stay distinct', 'A title-level work remains separate from each physical or digital edition, publisher, language, format and identifier.'),
    card('Series', 'Statements stay as catalogued', 'Series relationships are presented from the catalogue rather than reconstructed into an invented reading order.'),
    card('Private library', 'Shelves, ratings and goals stay yours', 'Reading state, ratings, notes, lists and goals are kept on the device rather than turned into shop recommendations.'),
  ],
  'book-explorer:sources:facts': [
    fact('Catalogue', 'Every answer comes from the VISINVOKE book catalogue'),
    fact('Commerce', 'No adverts and no shop links'),
    fact('Privacy', 'Your library stays on your device'),
  ],
  'food-nutrition:what:cards': [
    card('Measures', 'Per 100 g or published serving', 'Switch between a comparable 100 gram basis and the serving supplied with the source record.'),
    card('Energy', 'Kilocalories and kilojoules together', 'Energy remains available in both common units instead of forcing a hidden conversion.'),
    card('Detail', 'Macros, vitamins and minerals', 'Open the full nutrient panel while keeping the data type and source record visible.'),
  ],
  'food-nutrition:sources:facts': [
    fact('Primary source', 'USDA FoodData Central'),
    fact('Commerce', 'No adverts and no shop links'),
    fact('Measures', 'Per 100 g or a published serving'),
  ],
  'packaged-food-scanner:what:cards': [
    card('Ingredients', 'Read what the pack publishes', 'Ingredients remain in the supplied order and are not silently rewritten into a different product claim.'),
    card('Scores', 'Published Nutri-Score and NOVA', 'Published scores are shown as source data, alongside the record they came from.'),
    card('Privacy', 'Scans remain on the device', 'Barcode capture identifies the product without turning scan history into an advertising profile.'),
  ],
  'packaged-food-scanner:sources:facts': [
    fact('Primary source', 'Open Food Facts'),
    fact('Licence', 'Open Database Licence data'),
    fact('Privacy', 'Favourites and scans stay on your device'),
  ],
  'knowledge-explorer:what:cards': [
    card('Fact pages', 'Dates, places and identifiers together', 'A result opens as a structured record rather than a loose list of unrelated search snippets.'),
    card('Precision', 'Dates keep what is actually known', 'Year-only, month-level and full dates stay distinct instead of being padded with invented precision.'),
    card('Graph', 'Follow the entries a fact connects to', 'People, places, events, organisations, works and ideas remain navigable through their explicit relationships.'),
  ],
  'knowledge-explorer:sources:facts': [
    fact('Primary source', 'Wikidata structured data'),
    fact('Licence', 'CC0'),
    fact('Commerce', 'No adverts or tracking'),
  ],
  'art-museum-explorer:what:cards': [
    card('Records', 'Dates, media and classifications', 'Each artwork retains the museum record fields that describe the object rather than reducing it to an image.'),
    card('Artists', 'Move from an artist to their works', 'Artist pages connect the person to catalogued works across the two included collections.'),
    card('Rights', 'Public-domain flags remain attributed', 'Rights information is shown as published by the contributing museum rather than inferred from the image alone.'),
  ],
  'art-museum-explorer:sources:facts': [
    fact('Collections', 'The Met and Smithsonian open-access records'),
    fact('Record links', 'The museum record remains available'),
    fact('Commerce', 'No adverts and no shop links'),
  ],
  'travel-guide:what:cards': [
    card('Listings', 'See, do, eat, drink and sleep', 'Listings retain hours, prices and coordinates where the source guide provides them.'),
    card('Context', 'Sub-destinations and nearby places', 'A guide stays connected to the surrounding places that make a practical trip possible.'),
    card('Featured', 'Start from a complete guide', 'Featured destinations make it easier to explore without turning the page into advertising or booking links.'),
  ],
  'travel-guide:sources:facts': [
    fact('Primary source', 'Wikivoyage community travel text'),
    fact('Licence', 'CC BY-SA'),
    fact('Commerce', 'No booking links or adverts'),
  ],
  'space-explorer:what:cards': [
    card('Candidates', 'TESS, Kepler and K2', 'Mission candidates remain identified as candidates rather than being displayed as confirmed planets.'),
    card('Measurements', 'Uncertainty remains visible', 'Ranges and uncertainties stay attached to measured properties so a single number does not imply false certainty.'),
    card('References', 'Citations travel with the record', 'Planet, system and candidate pages retain the references behind their discovery and measured properties.'),
  ],
  'space-explorer:sources:facts': [
    fact('Primary source', 'NASA Exoplanet Archive tables'),
    fact('Coverage', 'Confirmed planets, systems and mission candidates'),
    fact('Evidence', 'Citations on every page'),
  ],
  'climate-explorer:what:cards': [
    card('History', 'Open any year day by day', 'Daily observations support close inspection instead of limiting the record to a single summary chart.'),
    card('Extremes', 'Warmest, coolest, wettest and snowiest', 'Station extremes remain connected to the year and record period that produced them.'),
    card('Date', 'See what happened on this day', 'A calendar date can be followed through the station history without pretending missing observations exist.'),
  ],
  'climate-explorer:sources:facts': [
    fact('Primary source', 'NOAA U.S. station records'),
    fact('Normals', '1991–2020 climate normals'),
    fact('Coverage', 'Long station records dating back to 1849'),
  ],
  'recipes:what:cards': [
    card('Scale', 'Change servings without mental arithmetic', 'Ingredient quantities scale with the serving count and can switch between metric and U.S. units.'),
    card('Pantry', 'Find what you can cook', 'Available ingredients can narrow the library to recipes that fit what is already in the kitchen.'),
    card('Private recipes', 'Keep your own recipes locally', 'Personal recipes remain on the device alongside the hosted library rather than becoming public content.'),
  ],
  'recipes:sources:facts': [
    fact('Primary source', 'Wikibooks Cookbook'),
    fact('Privacy', 'Your own recipes stay on your device'),
    fact('Commerce', 'No adverts or tracking'),
  ],

  'backup-verification-cloud:plans:facts': plannedFacts('Verification runs on an approved computer or runner'),
  'contractor-compliance-vault:plans:facts': plannedFacts('OCR and document processing run locally'),
  'credential-renewal-manager:plans:facts': plannedFacts('Document processing runs locally'),
  'compliance-evidence-vault:plans:facts': plannedFacts('Hashing, OCR, redaction and packaging run locally'),
  'maintenance-and-inspection-cloud:plans:facts': plannedFacts('Large media and analytics stay on your computer'),
  'secure-client-document-portal:plans:facts': plannedFacts('Preparation, redaction and packaging run locally'),

  'website-dns-ssl-uptime-monitor:what:cards': [
    card('Regions', 'Independent observations stay independent', 'Regional checks keep their time, origin and result so a quorum is based on evidence rather than one repeated answer.'),
    card('Private probes', 'Internal origins remain private', 'Approved local probes can compare internal and public behaviour without publishing private targets or credentials.'),
    card('Formula', 'Uptime remains explicit', 'Availability calculations retain the policy, maintenance treatment and observation set used to produce the number.'),
  ],
  'website-dns-ssl-uptime-monitor:response:cards': [
    card('Correlation', 'One incident, not a storm of symptoms', 'Related HTTP, DNS and TLS failures can be joined into one incident without erasing the underlying observations.'),
    card('Maintenance', 'Planned work is a recorded decision', 'Maintenance windows remain scoped and auditable instead of silently disappearing from the history.'),
    card('Recovery', 'Resolution requires fresh evidence', 'An incident closes from confirmed healthy observations, not from an alert simply becoming quiet.'),
  ],
  'service-command-centre:what:cards': [
    card('Flow', 'A booking becomes operational work', 'The accepted booking opens the connected job path, carrying customer, device, schedule and service context forward.'),
    card('Stock', 'Parts follow the job', 'Inventory and supplier records connect parts reservation, usage and replenishment to the work that required them.'),
    card('Finance', 'Completion reaches invoice and payment', 'Captured labour and parts continue into quoting, invoicing, payment and operational reporting without rebuilding the record.'),
  ],
  'website-capture-suite:capabilities:cards': [
    card('Structure', 'Pages become queryable records', 'Metadata, links, assets, content and responsive captures remain joined to the page they describe.'),
    card('Control', 'Scope and crawl rules are deliberate', 'Allowed domains, rendering behaviour and capture sizes are set for the project instead of left to an opaque crawler default.'),
    card('Output', 'Results support the next job', 'The structured database can support audit, migration, compliance, documentation and authorised AI processing.'),
  ],
  'track-lock-wipe:actions:cards': [
    card('4 · Verify', 'Confirm the device setup first', 'Supported controls, ownership and recovery contact are checked before an incident so the available response is known in advance.'),
    card('5 · Record', 'Keep the response traceable', 'Requests, identity checks, issued actions and reported outcomes stay connected to the registered device.'),
    card('6 · Escalate', 'Choose the proportionate next step', 'Location evidence, reachability and the value of the information guide whether to wait, lock, wipe or seek further help.'),
  ],
};

export function completeThreePanelBlock(productKey, sectionId, block) {
  if (!['cards', 'facts'].includes(block.t) || block.items?.length !== 3) return block;
  const key = `${productKey}:${sectionId}:${block.t}`;
  const additions = PRODUCT_PANEL_ADDITIONS[key];
  if (!additions || additions.length !== 3) return block;
  return { ...block, cols: 3, items: [...block.items, ...additions] };
}

export default PRODUCT_PANEL_ADDITIONS;
